// Small helper around Google Maps' Geocoder to reverse-geocode a lat/lng into
// normalized address components. Assumes @react-google-maps/api has already
// loaded the JS API (i.e. we're in a page that mounted <GoogleMap>).
// Returns { city, state, country, zip_code, formatted_address, street } or
// null on failure. City is normalized (title-case, no accents variants) to
// keep the DB consistent.

const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const titleCase = (s) => (s || '')
  .toLowerCase()
  .split(' ')
  .map(w => w ? w[0].toUpperCase() + w.slice(1) : '')
  .join(' ')
  .trim();

// A few well-known city aliases → canonical name (Mexican bias since Bookvia
// launches in MX; harmless everywhere else).
const CITY_ALIASES = {
  'ciudad de mexico': 'Ciudad de México',
  'cdmx': 'Ciudad de México',
  'df': 'Ciudad de México',
  'distrito federal': 'Ciudad de México',
  'mexico city': 'Ciudad de México',
  'monterrey': 'Monterrey',
  'guadalajara': 'Guadalajara',
  'queretaro': 'Querétaro',
  'quéretaro': 'Querétaro',
  'puebla': 'Puebla',
  'merida': 'Mérida',
  'mérida': 'Mérida',
  'cancun': 'Cancún',
  'cancún': 'Cancún',
  'tijuana': 'Tijuana',
  'leon': 'León',
  'león': 'León',
};

const normalizeCity = (raw) => {
  if (!raw) return '';
  const key = stripAccents(raw).toLowerCase().trim();
  if (CITY_ALIASES[key]) return CITY_ALIASES[key];
  // Also match against non-accent alias keys
  const noAccentKey = stripAccents(key);
  if (CITY_ALIASES[noAccentKey]) return CITY_ALIASES[noAccentKey];
  return titleCase(raw);
};

// Public: normalize any city string coming from Google Places / raw user input.
export const normalizeCityName = normalizeCity;

/**
 * Reverse-geocodes a coordinate pair. Requires window.google.maps to be loaded.
 * Returns null on failure (never throws).
 */
export async function reverseGeocode(lat, lng) {
  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) return null;
  if (lat == null || lng == null) return null;

  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ location: { lat: Number(lat), lng: Number(lng) } }, (results, status) => {
      if (status !== 'OK' || !results || results.length === 0) {
        resolve(null);
        return;
      }
      const primary = results[0];
      const components = primary.address_components || [];

      const findComponent = (types) => {
        const match = components.find(c => types.some(t => c.types.includes(t)));
        return match ? { long: match.long_name, short: match.short_name } : null;
      };

      // Google buckets Mexican cities in different levels depending on state.
      // Try locality → sublocality → administrative_area_level_2 in order.
      const cityRaw = findComponent(['locality'])?.long
        || findComponent(['sublocality', 'sublocality_level_1'])?.long
        || findComponent(['administrative_area_level_2'])?.long
        || '';
      const state = findComponent(['administrative_area_level_1']);
      const country = findComponent(['country']);
      const zip = findComponent(['postal_code']);
      const streetNumber = findComponent(['street_number'])?.long || '';
      const route = findComponent(['route'])?.long || '';
      const street = [route, streetNumber].filter(Boolean).join(' ').trim();

      resolve({
        city: normalizeCity(cityRaw),
        state: state ? state.long : '',
        country: country ? country.short : '', // ISO alpha-2 (MX, US, etc.)
        zip_code: zip ? zip.long : '',
        street,
        formatted_address: primary.formatted_address || '',
      });
    });
  });
}
